import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert } from "react-bootstrap";
import { FaArrowRight } from "react-icons/fa";
import api from "../../services/api";
import "./Properties.css";

// AeroIndustrialHubPage component
const AeroIndustrialHubPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lotTypes, setLotTypes] = useState([]);

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        // Get lot data from API using our configured service
        const lotsResponse = await api.get("/public/properties/aero-industrial-hub/lots");
        
        if (lotsResponse.data && Array.isArray(lotsResponse.data)) {
          // Process data
          processLotData(lotsResponse.data);
        } else {
          console.error("No data returned from API or invalid format");
          setError("Failed to load property data. Please try again later.");
          setLotTypes([]);
        }
      } catch (err) {
        console.error("Error fetching property data:", err);
        setError("Failed to load property data. Please try again later.");
        setLotTypes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyData();
  }, []);

  // Helper function to process lot data from API
  const processLotData = (lotsData) => {
    const types = ["A", "B", "C"];
    const typeData = types.map(type => {
      // Filter lots by their type (A, B, or C)
      const lotsOfType = lotsData.filter(lot => lot.type === type);
      
      // Count lots by status, normalizing case
      const availableCount = lotsOfType.filter(lot => 
        typeof lot.status === 'string' && lot.status.toLowerCase() === "available"
      ).length;
      
      const reservedCount = lotsOfType.filter(lot => 
        typeof lot.status === 'string' && lot.status.toLowerCase() === "reserved"
      ).length;

      const bookedCount = lotsOfType.filter(lot => 
        typeof lot.status === 'string' && lot.status.toLowerCase() === "booked"
      ).length;
      
      const soldCount = lotsOfType.filter(lot => 
        typeof lot.status === 'string' && (
          lot.status.toLowerCase() === "sold" || 
          lot.status.toLowerCase() === "locked"
        )
      ).length;
      
      return {
        id: type,
        name: `Type ${type}`,
        description: getTypeDescription(type),
        lotsCount: lotsOfType.length,
        availableCount: availableCount,
        reservedCount: reservedCount,
        bookedCount: bookedCount,
        soldCount: soldCount
      };
    });
    
    setLotTypes(typeData);
  };

  // Helper function for type descriptions
  const getTypeDescription = (type) => {
    switch (type) {
      case "A":
        return "Premium corner units with excellent visibility and dual access points, perfect for showrooms or businesses requiring high visibility.";
      case "B":
        return "Versatile intermediate units ideal for manufacturing, warehousing, or distribution centers with efficient space utilization.";
      case "C":
        return "Spacious end units with additional side access, suitable for businesses requiring larger operational areas and multiple entry points.";
      default:
        return "Industrial lot with good accessibility.";
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <div className="property-types-page">
      <Container className="py-5">
        {error && <Alert variant="danger">{error}</Alert>}
        
        {lotTypes.length === 0 && !error ? (
          <Alert variant="info">No property data available at the moment.</Alert>
        ) : (
          <>
            <div className="page-header text-center mb-5">
              <h1>Aero Industrial Hub Properties</h1>
              <p className="lead">
                Explore our premium industrial units designed to meet diverse business requirements
              </p>
            </div>

            <div className="property-types-container">
              {lotTypes.map((type) => (
                <div className="property-type-item" key={type.id}>
                  <Card className="property-type-card">
                    <Card.Body>
                      <Card.Title className="type-title">
                        <h2>{type.name}</h2>
                      </Card.Title>
                      
                      <div className="type-description">
                        <p>{type.description}</p>
                      </div>
                      
                      <div className="type-stats">
                        <div className="stat-card available">
                          <div className="stat-number">{type.availableCount}</div>
                          <div className="stat-label">Available</div>
                        </div>
                        <div className="stat-card reserved">
                          <div className="stat-number">{type.reservedCount}</div>
                          <div className="stat-label">Reserved</div>
                        </div>
                        <div className="stat-card booked">
                          <div className="stat-number">{type.bookedCount}</div>
                          <div className="stat-label">Booked</div>
                        </div>
                        <div className="stat-card sold">
                          <div className="stat-number">{type.soldCount}</div>
                          <div className="stat-label">Sold</div>
                        </div>
                        <div className="stat-card total">
                          <div className="stat-number">{type.lotsCount}</div>
                          <div className="stat-label">Total</div>
                        </div>
                      </div>
                      
                      <div className="type-actions">
                        <Link to={`/properties/aeroindustrial/type/${type.id}`}>
                          <Button variant="primary" className="view-details-btn">
                            View Details <FaArrowRight className="ms-1" />
                          </Button>
                        </Link>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default AeroIndustrialHubPage; 